import { ReactNode } from 'react';
import {
  CellTemplate,
  Compatible,
  DateCell,
  getCharFromKey,
  isCharAlphaNumeric,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from '@silevis/reactgrid';
import cn from 'classnames';
import { normalizeDateInput } from 'src/entity/CellTemplates/DateCellTemplate/helpers';
import { TableDatePicker } from 'src/entity/CellTemplates/DateCellTemplate/TableDatePicker';
import { ReactComponent as DatePickerIcon } from 'src/shared/assets/icons/kit/date-picker.svg';
import { isTimeStamp } from 'src/shared/lib/utils';

export interface MyDateCell extends Omit<DateCell, 'date'> {
  date?: string | number;
  dateOpen?: string;
  dateKey?: string;
}

const getFormattedTimeUnit = (timeUnit: number): string => timeUnit.toString().padStart(2, '0');

const getFormattedDate = (date: Date) => {
  const year = getFormattedTimeUnit(date.getFullYear());
  const month = getFormattedTimeUnit(date.getMonth() + 1);
  const day = getFormattedTimeUnit(date.getDate());

  return `${day}-${month}-${year}`;
};

export class DateCellTemplate implements CellTemplate<MyDateCell> {
  private wasEscKeyPressed = false;

  getCompatibleCell(uncertainCell: Uncertain<MyDateCell>): Compatible<MyDateCell> {
    if (!uncertainCell.date) {
      return { ...uncertainCell, date: '', value: 0, text: '' };
    }

    if (isTimeStamp(uncertainCell.date)) {
      const myDate = new Date(+uncertainCell?.date * 1000 || '');
      const text = getFormattedDate(myDate);

      return { ...uncertainCell, value: 0, text };
    }

    const normalizedDate = normalizeDateInput(uncertainCell.date);

    return {
      ...uncertainCell,
      date: normalizedDate ?? uncertainCell.date.toString(),
      value: 0,
      text: normalizedDate ?? uncertainCell.date.toString(),
    };
  }

  handleKeyDown(
    cell: Compatible<MyDateCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
    key: string,
    capsLock: boolean,
  ): { cell: Compatible<MyDateCell>; enableEditMode: boolean } {
    if (ctrl || keyCode === 37 || keyCode === 38 || keyCode === 39 || keyCode === 40) {
      return { cell, enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER };
    }

    if (!ctrl && isCharAlphaNumeric(getCharFromKey(key))) {
      return {
        cell: this.getCompatibleCell({
          ...cell,
          dateOpen: cell.dateOpen === cell.dateKey ? undefined : cell.dateKey,
        }),
        enableEditMode: true,
      };
    }

    return {
      cell: {
        ...cell,
        dateOpen: cell.dateOpen === cell.dateKey ? undefined : cell.dateKey,
      },
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  update(
    cell: Compatible<MyDateCell>,
    cellToMerge: UncertainCompatible<MyDateCell>,
  ): Compatible<MyDateCell> {
    const incomingDate =
      cellToMerge.date !== undefined && cellToMerge.date !== ''
        ? cellToMerge.date
        : cellToMerge.text;
    const normalizedDate = normalizeDateInput(incomingDate);

    return this.getCompatibleCell({
      ...cell,
      dateOpen: cellToMerge.dateOpen,
      dateKey: cellToMerge.dateKey,
      date: normalizedDate ?? incomingDate ?? '',
    });
  }

  getClassName(cell: Compatible<MyDateCell>, isInEditMode: boolean): string {
    return cell.className ? cell.className : '';
  }

  render(
    cell: Compatible<MyDateCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<MyDateCell>, commit: boolean) => void,
  ): ReactNode {
    const myDate = cell?.date;

    const getDate = (date?: string | number) => {
      if (!date) {
        return new Date();
      }

      if (!isNaN(+date)) {
        return new Date(+date * 1000);
      }

      const normalizedDate = normalizeDateInput(date);
      if (!normalizedDate) {
        return new Date();
      }

      const [d, m, y] = normalizedDate.split('-');

      return new Date(Number(y), Number(m) - 1, Number(d));
    };

    if (!isInEditMode) {
      return (
        <>
          {cell.text}
          <div
            {...(cell.dateKey === cell.dateOpen && { id: 'date-picker' })}
            className={
              'group absolute right-0 top-0 flex aspect-square h-full cursor-pointer ' +
              'items-center justify-center'
            }
            onClick={() => {
              onCellChanged(
                this.getCompatibleCell({ ...cell, dateOpen: cell.dateKey }),
                !this.wasEscKeyPressed,
              );
            }}
          >
            <DatePickerIcon
              className={cn(
                {
                  'fill-grey-400 group-hover:fill-blue-300': !Boolean(
                    cell.dateKey === cell.dateOpen,
                  ),
                  'fill-blue-300': Boolean(cell.dateKey === cell.dateOpen),
                },
                'h-4 w-4',
              )}
            />
          </div>
          {cell.dateKey === cell.dateOpen && (
            <TableDatePicker
              markedDate={getDate(myDate)}
              onClose={() => {
                onCellChanged(
                  this.getCompatibleCell({ ...cell, dateOpen: undefined }),
                  !this.wasEscKeyPressed,
                );
              }}
              onClick={date => {
                onCellChanged(
                  this.getCompatibleCell({
                    ...cell,
                    dateOpen: undefined,
                    date: getFormattedDate(date),
                  }),
                  !this.wasEscKeyPressed,
                );
              }}
            />
          )}
        </>
      );
    }

    return (
      <div className="relative h-full w-full">
        <input
          className="rg-input"
          ref={input => {
            if (input) input.focus();
          }}
          defaultValue={cell.text}
          onChange={e => {
            if (e.currentTarget.value) {
              onCellChanged(
                this.getCompatibleCell({ ...cell, date: e.currentTarget.value }),
                false,
              );
            }
          }}
          onBlur={e => {
            if (e.currentTarget.value) {
              onCellChanged(
                this.getCompatibleCell({ ...cell, date: e.currentTarget.value }),
                !this.wasEscKeyPressed,
              );
              this.wasEscKeyPressed = false;
            }
          }}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
              e.stopPropagation();
              return;
            }
            if (e.keyCode === keyCodes.ESCAPE) this.wasEscKeyPressed = true;
          }}
          onCopy={e => e.stopPropagation()}
          onCut={e => e.stopPropagation()}
          onPaste={e => {
            e.stopPropagation();

            const pastedText =
              e.clipboardData.getData('text/plain') || e.clipboardData.getData('text');
            const normalizedDate = normalizeDateInput(pastedText);
            const nextDate = normalizedDate ?? pastedText.trim();

            if (!nextDate) {
              return;
            }

            e.preventDefault();
            e.currentTarget.value = nextDate;
            onCellChanged(this.getCompatibleCell({ ...cell, date: nextDate }), false);
          }}
          onPointerDown={e => e.stopPropagation()}
        />
      </div>
    );
  }
}
